<template>
  <el-upload
    class="upload-demo"
    ref="upload"
    :limit="fileQuantityLimit"
    action=""
    :accept="acceptExtensionForComponent"
    :on-change="handleChange"
    :on-remove="handleRemove"
    :auto-upload="false"
    :file-list="fileList"
    :list-type="listType"
    :multiple="(fileQuantityLimit>1)?true:false">
    <el-button slot="trigger" size="small" type="primary" v-show="fileList.length<fileQuantityLimit">選擇檔案</el-button>
    <!-- 有壓縮時改用自訂列表項目，顯示壓縮前後的檔案大小 -->
    <template v-if="compressToMB!==null&&compressToMB!==undefined" v-slot:file="{ file }">
      <img v-if="listType==='picture'" class="el-upload-list__item-thumbnail" :src="file.url" alt="">
      <a class="el-upload-list__item-name file-upload__name">{{file.name}}</a>
      <div class="file-upload__size" :class="{ 'is-failed': file.compressStatus==='failed' }">
        <span v-if="file.compressStatus==='compressing'"><i class="el-icon-loading"></i> 壓縮中...（原始 {{formatFileSize(file.originalSize)}}）</span>
        <span v-else-if="file.compressStatus==='done'">{{formatFileSize(file.originalSize)}} → 壓縮後 {{formatFileSize(file.size)}}</span>
        <span v-else-if="file.compressStatus==='failed'">{{formatFileSize(file.size)}}（壓縮失敗，將上傳原檔）</span>
        <span v-else>{{formatFileSize(file.size)}}</span>
      </div>
      <i class="el-icon-close" @click="$refs.upload.handleRemove(file)"></i>
    </template>
    <div slot="tip" class="el-upload__tip" style="line-height: 1.5rem;">
      <span v-if="acceptExtensionForMessage!=''" style="margin-right: 1rem;">檔案格式必須為{{acceptExtensionForMessage}}<br></span>
      <span v-if="limitSizeOfMB!==null&&limitSizeOfMB!==undefined">檔案大小不得超過{{limitSizeOfMB}}MB<br></span>
      <span v-if="compressToMB!==null&&compressToMB!==undefined">超過{{compressToMB}}MB的圖片會自動壓縮（GIF除外）<br></span>
      <span v-if="fileQuantityLimit!==null&&fileQuantityLimit!==undefined&&fileQuantityLimit>1">單次最多上傳{{fileQuantityLimit}}張<br></span>
      <span v-if="pictureSizeRemark!==null&&pictureSizeRemark!==undefined">建議尺寸 : {{pictureSizeRemark}}<br></span>
    </div>
  </el-upload>
</template>

<script>
import { compressImage, formatFileSize } from '@/assets/js/imageCompress';

export default {
  data() {
    return {
      acceptExtensionForComponent: '', // 給上傳元件用來限制上傳檔名(在選擇檔案視窗會限制副檔名)
      acceptExtensionForMessage: '', // 限定格式的提示字
    };
  },
  props: {
    fileQuantityLimit: Number, // 上傳數量限制
    acceptExtensions: Array, // 可上傳的副檔名陣列
    limitSizeOfMB: Number, // 單一檔案大小限制
    fileList: Array, // fileList同時會利用emit向外更新
    listType: String, // picture or text
    pictureSizeRemark: String, //
    compressToMB: Number, // 圖片超過此大小時自動壓縮至此大小以內
  },
  created() {
    this.compressQueue = Promise.resolve(); // 壓縮佇列，一次只壓一張避免多張大圖同時佔用大量記憶體
  },
  methods: {
    handleChange(file, fileList) {
      let extensionCheck = true;
      // 有限制副檔名的狀態才須驗證
      if (this.acceptExtensions !== null && this.acceptExtensions !== undefined && this.acceptExtensions.length > 0) {
        if (this.acceptExtensions.includes(this.getFileExtension(file.name))) {
          extensionCheck = true;
        } else {
          extensionCheck = false;
        }
      }
      const sizeCheck = this.limitSizeOfMB == null || file.size / 1024 / 1024 < this.limitSizeOfMB;

      if (!extensionCheck) {
        this.$message.error(`檔案格式必須為${this.acceptExtensionForMessage} !`);
        fileList.splice(fileList.length - 1, 1);
      } else if (!sizeCheck) {
        this.$message.error(`檔案大小不得超過${this.limitSizeOfMB}MB!`);
        fileList.splice(fileList.length - 1, 1);
      } else {
        if (this.compressToMB !== null && this.compressToMB !== undefined) {
          this.compressFile(file);
        }
        this.$emit('update:fileList', fileList);
      }
    },
    handleRemove(file, fileList) {
      this.$emit('update:fileList', fileList);
    },
    compressFile(file) {
      this.setFileProps(file, { originalSize: file.size, compressStatus: 'compressing' });
      const task = this.compressQueue
        .then(() => {
          // 輪到時已被移除(或列表被清空)就不用壓了
          if (!this.$refs.upload || !this.$refs.upload.uploadFiles.includes(file)) return file.raw;
          return compressImage(file.raw, this.compressToMB * 1024 * 1024);
        })
        .then((compressed) => {
          const compressStatus = compressed === file.raw ? 'none' : 'done';
          this.setFileProps(file, {
            raw: compressed,
            name: compressed.name,
            size: compressed.size,
            compressStatus,
          });
        })
        .catch(() => {
          this.setFileProps(file, { compressStatus: 'failed' });
        });
      this.compressQueue = task;
      this.setFileProps(file, { compressTask: task });
    },
    setFileProps(file, props) {
      Object.keys(props).forEach((key) => {
        this.$set(file, key, props[key]);
      });
    },
    formatFileSize,
    getFileExtension(filename) {
      return (/[.]/.exec(filename)) ? `.${/[^.]+$/.exec(filename)[0]}`.toLowerCase() : undefined;
    },
  },
  mounted() {
    if (this.acceptExtensions !== null && this.acceptExtensions !== undefined) {
      for (let i = 0; i < this.acceptExtensions.length; i += 1) {
        if (i !== 0) {
          this.acceptExtensionForComponent += ',';
          this.acceptExtensionForMessage += '、';
        }
        const extension = this.acceptExtensions[i];
        this.acceptExtensionForComponent += extension;
        this.acceptExtensionForMessage += extension;
      }
    }
  },
  watch: {
    fileList() {
      if (this.fileList === null || this.fileList === undefined || this.fileList.length === 0) {
        this.$refs.upload.clearFiles(); // 監控prop的filelist被父原件清空時，應該將列表歸0。
      }
    },
  },
};
</script>

<style lang="scss">
  // 移除過渡動畫
  .el-upload-list__item {
    transition: none !important;
  }
  // 自訂列表項目：檔名下方顯示檔案大小
  .el-upload-list--picture .el-upload-list__item-name.file-upload__name {
    line-height: 1.5;
    margin-top: 14px;
  }
  .file-upload__size {
    padding-left: 4px;
    font-size: 12px;
    line-height: 1.5;
    color: #909399;
    &.is-failed {
      color: #F56C6C;
    }
  }
</style>
